import axios from 'axios'
import { ActionTree } from 'vuex'
import _ from 'lodash'
import moment from 'moment'
import { RootState } from '@/store/types'
import { MailTemplatesState, MailTemplateConditions, MailTemplate } from '@/store/mails/templates/types'
import { getPagerFromApiResponse } from '@/store/utils'

const actions: ActionTree<MailTemplatesState, RootState> = {
  /**
   * メールテンプレート一覧取得
   * @param {{
   *  conditions: MailTemplateConditions, // テンプレート検索条件
    * page: number, // ページ番号
   * }}
   */
  fetchMailTemplates: async (
    { commit, dispatch, getters },
    { conditions, page }: { conditions?: MailTemplateConditions, page: number },
  ) => {
    const { headers, data } = await axios.get('/mailTemplates', {
      params: {
        page,
        ..._.pickBy({
          name: conditions?.name,
          universityCode: conditions?.university?.code,
          targetType: conditions?.targets?.join(','),
          month: conditions?.month,
        }),
      },
    })

    await Promise.all([
      dispatch('fetchMasterUniversities'),
    ])

    const templates = _.map(data, (t): MailTemplate => ({
      id: t.mailTemplateId,
      university: getters.findUniversityByCode(t.universityCode),
      name: t.mailTemplateName,
      target: t.targetType,
      notification: t.notificationType,
      month: t.month,
      subject: t.subject,
      body: t.body,
      created: moment(t.created, 'YYYYMMDDHHmm'),
      updated: moment(t.updated, 'YYYYMMDDHHmm'),
    }))

    commit('fetchedMailTemplates', {
      pager: getPagerFromApiResponse(page, headers),
      templates,
    })
  },

  /**
   * メールテンプレート一見取得
   * @param {string} mailTemplateId メールテンプレートID
   */
  getMailTemplate: async (
    { commit, dispatch, getters },
    mailTemplateId: string,
  ) => {
    const [res] = await Promise.all([
      axios.get(`/mailTemplates/${mailTemplateId}`),
      dispatch('fetchMasterUniversities'),
    ])

    const { data } = res
    const template: MailTemplate = {
      id: data.mailTemplateId,
      university: getters.findUniversityByCode(data.universityCode),
      name: data.mailTemplateName,
      target: data.targetType,
      notification: data.notificationType,
      month: data.month,
      subject: data.subject,
      body: data.body,
    }
    commit('gotMailTemplate', template)
  },

  /**
   * メールテンプレート登録
   * @param {MailTemplate} template メールテンプレート
   */
  registerMailTemplate: (context, template: MailTemplate) => {
    return axios.post(
      '/mailTemplates',
      {
        universityCode: template.university.code,
        name: template.name,
        targetType: template.target,
        notificationType: template.notification,
        month: template.month,
        subject: template.subject,
        body: template.body,
      },
    )
  },

  /**
   * メールテンプレート編集
   * @param {{
   *  mailTemplateId: string, // メールテンプレートID
   *  template: MailTemplate, // メールテンプレート
   * }}
   */
  editMailTemplate: (
    context,
    { mailTemplateId, template }: { mailTemplateId: string, template: MailTemplate },
  ) => {
    return axios.put(
      `/mailTemplates/${mailTemplateId}`,
      {
        universityCode: template.university.code,
        name: template.name,
        targetType: template.target,
        notificationType: template.notification,
        month: template.month,
        subject: template.subject,
        body: template.body,
      },
    )
  },

  /**
   * メールテンプレート削除
   * @param {string} mailTemplateId メールテンプレートID
   */
  deleteMailTemplate: (
    context,
    mailTemplateId: string,
  ) => axios.delete(`/mailTemplates/${mailTemplateId}`),
}

export default actions
